import React from 'react';
import { usePriceFeed } from '../context/PriceFeedContext';
import { useTrade } from '../context/TradeProvider';
import { getDigitsFor } from '../utils/symbolDigitsStore';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle2, TrendingDown, TrendingUp } from 'lucide-react';

const TradeControls = ({
  tradeDirection,
  setTradeDirection,
  orderType,
  setOrderType,
  limitEntryPrice,
  setLimitEntryPrice,
}) => {
  const groupLabelClassName = 'text-[11px] font-medium text-textSecondary';
  const orderTypeButtonClassName = 'trade-panel-toggle-button';
  const { bid: bidPrice, ask: askPrice } = usePriceFeed();
  const { accountId, symbol } = useTrade();
  const cachedDigits = getDigitsFor(accountId, symbol);
  const decimals = Number.isFinite(cachedDigits) ? cachedDigits : 5;
  const isBuySelected = tradeDirection === 'BUY';
  const isSellSelected = tradeDirection === 'SELL';

  return (
    <div className="space-y-3">
      {/* Order type row */}
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center">
          <label className={`${groupLabelClassName} mb-1.5`}>Order Type</label>
          <div className="flex gap-1">
            <Button
              variant={orderType === 'MARKET' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setOrderType('MARKET')}
              className={orderTypeButtonClassName}
            >
              Market
            </Button>
            <Button
              variant={orderType === 'LIMIT' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setOrderType('LIMIT')}
              className={orderTypeButtonClassName}
            >
              Limit
            </Button>
          </div>
        </div>
      </div>
      {/* Buy / Sell row */}
      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Trade direction">
        <Button
          onClick={() => setTradeDirection('BUY')}
          variant={isBuySelected ? 'success' : 'outline'}
          aria-pressed={isBuySelected}
          className={`relative min-h-[64px] w-full overflow-hidden border-2 px-2.5 py-2 transition-all duration-150 ${
            isBuySelected
              ? 'border-textAccent shadow-[0_8px_18px_rgba(0,0,0,0.22)] ring-2 ring-success'
              : 'border-success bg-backgroundSurface text-success hover:border-successHover hover:bg-success hover:text-textAccent'
          }`}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <TrendingUp className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="flex min-w-0 flex-col items-start leading-tight">
                <span className="text-sm font-medium">Buy</span>
                <span className="font-mono text-[12px] font-medium">{typeof askPrice === 'number' ? askPrice.toFixed(decimals) : 'N/A'}</span>
              </span>
            </div>
            {isBuySelected && <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />}
          </div>
        </Button>
        <Button
          onClick={() => setTradeDirection('SELL')}
          variant={isSellSelected ? 'destructive' : 'outline'}
          aria-pressed={isSellSelected}
          className={`relative min-h-[64px] w-full overflow-hidden border-2 px-2.5 py-2 transition-all duration-150 ${
            isSellSelected
              ? 'border-textAccent shadow-[0_8px_18px_rgba(0,0,0,0.22)] ring-2 ring-destructive'
              : 'border-destructive bg-backgroundSurface text-destructive hover:border-destructiveHover hover:bg-destructive hover:text-textAccent'
          }`}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <TrendingDown className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="flex min-w-0 flex-col items-start leading-tight">
                <span className="text-sm font-medium">Sell</span>
                <span className="font-mono text-[12px] font-medium">{typeof bidPrice === 'number' ? bidPrice.toFixed(decimals) : 'N/A'}</span>
              </span>
            </div>
            {isSellSelected && <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />}
          </div>
        </Button>
      </div>
      {orderType === 'LIMIT' && (
        <div>
          <label className={`mb-1 block ${groupLabelClassName}`}>Entry Price</label>
          <Input
            type="number"
            value={limitEntryPrice}
            onChange={(e) => setLimitEntryPrice(e.target.value)}
          />
        </div>
      )}
    </div>
  );
};

export default TradeControls;
